import { s } from '@/theme/tokens';
import { FormCard } from '@/ui/composites/FormCard';
import { Inline } from '@/ui/layout/Inline';
import { Dialog, DialogContent, type DialogProps } from '@/ui/primitives';
import type { SpacingToken } from '@/theme/tokens';
import type { KeyboardEventHandler, ReactElement, ReactNode } from 'react';

type DialogLayoutProps = {
  open: boolean;
  title?: string;
  description?: ReactNode;
  actions?: ReactNode;
  actionsAlign?: 'flex-start' | 'center' | 'flex-end' | 'space-between';
  actionsGap?: SpacingToken;
  footerGap?: SpacingToken;
  contentGap?: SpacingToken;
  gap?: SpacingToken;
  contentSx?: DialogProps['sx'];
  onContentKeyDownCapture?: KeyboardEventHandler<HTMLDivElement>;
  onClose: () => void;
  disableClose?: boolean;
  allowTopBarInteraction?: boolean;
  maxWidth?: DialogProps['maxWidth'];
  children: ReactNode;
};

export const DialogLayout = ({
  open,
  title,
  description,
  actions,
  actionsAlign = 'flex-end',
  actionsGap = 'sm',
  footerGap = 'md',
  contentGap = 'md',
  gap = 'lg',
  contentSx,
  onContentKeyDownCapture,
  onClose,
  disableClose = false,
  allowTopBarInteraction = false,
  maxWidth = 'sm',
  children,
}: DialogLayoutProps): ReactElement => {
  const baseContentSx: DialogProps['sx'] = { overflow: 'visible' };
  const resolvedContentSx: DialogProps['sx'] = contentSx
    ? ([
        baseContentSx,
        ...(Array.isArray(contentSx) ? contentSx : [contentSx]),
      ] as DialogProps['sx'])
    : baseContentSx;
  const contentSxArray = Array.isArray(resolvedContentSx)
    ? resolvedContentSx
    : ([resolvedContentSx] as Array<DialogProps['sx']>);
  const footer = actions ? (
    <Inline gap={actionsGap} justify={actionsAlign}>
      {actions}
    </Inline>
  ) : undefined;
  const dialogSlotProps: DialogProps['slotProps'] = disableClose
    ? {
        paper: { sx: { p: s('none') } },
        backdrop: {
          onClick: (event) => {
            event.stopPropagation();
          },
        },
      }
    : { paper: { sx: { p: s('none') } } };
  const topBarOffsetSx: DialogProps['sx'] | undefined = allowTopBarInteraction
    ? {
        zIndex: 9,
        '& .MuiBackdrop-root': {
          top: 'var(--app-topbar-height, 54px)',
          height: 'calc(100% - var(--app-topbar-height, 54px))',
        },
        '& .MuiDialog-container': {
          top: 'var(--app-topbar-height, 54px)',
          height: 'calc(100% - var(--app-topbar-height, 54px))',
          maxHeight: 'calc(100% - var(--app-topbar-height, 54px))',
        },
      }
    : undefined;

  return (
    <Dialog
      open={open}
      onClose={disableClose ? undefined : onClose}
      disableEscapeKeyDown={disableClose}
      fullWidth
      maxWidth={maxWidth}
      slotProps={dialogSlotProps}
      sx={topBarOffsetSx}
    >
      <DialogContent
        sx={[{ p: s('none') }, ...contentSxArray]}
        onKeyDownCapture={onContentKeyDownCapture}
      >
        <FormCard
          container="none"
          title={title}
          description={description}
          headerGap="md"
          footer={footer}
          footerGap={footerGap}
          contentGap={contentGap}
          gap={gap}
        >
          {children}
        </FormCard>
      </DialogContent>
    </Dialog>
  );
};
